import React, { useState } from 'react';
import { useDraggablePanel } from '../utils/panelDragUtils';

interface ResourceSearchPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectResourceType: (resourceType: string) => void;
}

type ResourceCategory = 'fishing' | 'salvage' | 'forage' | 'mining';

const RESOURCE_TYPES: Record<ResourceCategory, { type: string; label: string }[]> = {
  fishing: [
    { type: 'shallow_waters', label: 'Shallow Waters' },
    { type: 'deep_sea', label: 'Deep Sea' },
    { type: 'coral_reef', label: 'Coral Reef' },
    { type: 'fishing_hole', label: 'Fishing Hole' },
    { type: 'shipwreck_pool', label: 'Shipwreck Pool' },
    { type: 'volcanic_waters', label: 'Volcanic Waters' },
    { type: 'abyssal_trench', label: 'Abyssal Trench' },
    { type: 'whirlpool_edge', label: 'Whirlpool Edge' },
    { type: 'moonlit_shallows', label: 'Moonlit Shallows' },
    { type: 'ancient_grounds', label: 'Ancient Grounds' },
  ],
  salvage: [
    { type: 'shipwreck_debris', label: 'Shipwreck Debris' },
    { type: 'sunken_rowboat', label: 'Sunken Rowboat' },
    { type: 'merchant_wreck', label: 'Merchant Wreck' },
    { type: 'abandoned_cargo', label: 'Abandoned Cargo' },
    { type: 'naval_wreck', label: 'Naval Wreck' },
    { type: 'pirate_cache', label: 'Pirate Cache' },
    { type: 'ancient_ruins', label: 'Ancient Ruins' },
    { type: 'sunken_temple', label: 'Sunken Temple' },
    { type: 'ghost_ship', label: 'Ghost Ship' },
    { type: 'titan_remains', label: 'Titan Remains' },
    { type: 'void_rift_debris', label: 'Void Rift Debris' },
    { type: 'lost_vault', label: 'Lost Vault' },
  ],
  forage: [
    { type: 'driftwood_cluster', label: 'Driftwood Cluster' },
    { type: 'coconut_palm', label: 'Coconut Palm' },
    { type: 'seaweed_bed', label: 'Seaweed Bed' },
    { type: 'herb_patch', label: 'Herb Patch' },
    { type: 'fruit_grove', label: 'Fruit Grove' },
    { type: 'mushroom_colony', label: 'Mushroom Colony' },
    { type: 'rare_flower_bed', label: 'Rare Flower Bed' },
    { type: 'ancient_tree', label: 'Ancient Tree' },
    { type: 'crystal_garden', label: 'Crystal Garden' },
    { type: 'bioluminescent_grove', label: 'Bioluminescent Grove' },
    { type: 'elder_growth', label: 'Elder Growth' },
    { type: 'world_tree_sapling', label: 'World Tree Sapling' },
    { type: 'void_bloom', label: 'Void Bloom' },
    { type: 'world_root_fragment', label: 'World Root Fragment' },
  ],
  mining: [
    { type: 'stone_quarry', label: 'Stone Quarry' },
    { type: 'tin_deposit', label: 'Tin Deposit' },
    { type: 'mineral_outcrop', label: 'Mineral Outcrop' },
    { type: 'silver_vein', label: 'Silver Vein' },
    { type: 'gold_deposit', label: 'Gold Deposit' },
    { type: 'coal_seam', label: 'Coal Seam' },
    { type: 'volcanic_vent', label: 'Volcanic Vent' },
    { type: 'astral_ore_deposit', label: 'Astral Ore Deposit' },
    { type: 'dusksteel_vein', label: 'Dusksteel Vein' },
    { type: 'sigilstone_deposit', label: 'Sigilstone Deposit' },
    { type: 'wyrmstone_ore', label: 'Wyrmstone Ore' },
    { type: 'void_crystal', label: 'Void Crystal' },
  ],
};

const CATEGORY_COLORS: Record<ResourceCategory, string> = {
  fishing: '#1e90ff',  // Dodger blue
  salvage: '#ff8c00',  // Dark orange
  forage: '#32cd32',   // Lime green
  mining: '#a9a9a9',   // Dark gray
};

export const ResourceSearchPanel: React.FC<ResourceSearchPanelProps> = ({
  isOpen,
  onClose,
  onSelectResourceType,
}) => {
  const [activeCategory, setActiveCategory] = useState<ResourceCategory>('fishing');
  const { style, panelRef, handleHeaderMouseDown } = useDraggablePanel(200, 300);

  if (!isOpen) return null;

  const handleSelectType = (resourceType: string) => {
    onSelectResourceType(resourceType);
    onClose();
  };

  return (
    <div className="resource-search-panel" style={style} ref={panelRef}>
      {/* Header */}
      <div className="resource-search-header" onMouseDown={handleHeaderMouseDown}>
        <span>🔍 Search Resources</span>
        <button className="resource-search-close" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* Category tabs */}
      <div className="resource-search-tabs">
        {(Object.keys(RESOURCE_TYPES) as ResourceCategory[]).map((category) => (
          <button
            key={category}
            className={`resource-search-tab ${activeCategory === category ? 'active' : ''}`}
            onClick={() => setActiveCategory(category)}
            style={
              activeCategory === category
                ? { borderBottomColor: CATEGORY_COLORS[category] }
                : {}
            }
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

      {/* Resource list */}
      <div className="resource-search-list">
        {RESOURCE_TYPES[activeCategory].map((resource) => (
          <button
            key={resource.type}
            className="resource-search-item"
            onClick={() => handleSelectType(resource.type)}
            style={{
              borderLeftColor: CATEGORY_COLORS[activeCategory],
            }}
          >
            <span className="resource-search-item-label">{resource.label}</span>
            <span className="resource-search-item-type">({resource.type})</span>
          </button>
        ))}
      </div>

      {/* Footer hint */}
      <div className="resource-search-footer">
        Click a resource type to find the nearest node
      </div>
    </div>
  );
};
